import { Dialog } from "primereact/dialog";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { downloadThreadAttachment } from "../../../api/routes";
import * as client from "../../../client";
import { downloadBlob } from "../../../util";
import styles from "./EmailThreadModal.module.css";

interface EmailThreadModalProps {
  visible: boolean;
  onHide: () => void;
  thread: client.EmailThread | null;
  reportId: string;
}

function formatDate(isoDate: string): string {
  try {
    const d = new Date(isoDate);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return isoDate;
  }
}

function formatShortDate(isoDate: string): string {
  try {
    const d = new Date(isoDate);
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return isoDate;
  }
}

/** Extract just the display name from a "Name <email>" string. */
function formatSender(from: string): string {
  const match = /^(.+?)\s*</.exec(from);
  return match ? match[1]!.trim() : from;
}

/** Get a plain-text snippet (first ~120 chars) for collapsed view. */
function getSnippet(message: client.EmailThreadMessage): string {
  const text = (message.body_text ?? "").trim();
  if (!text) return "";
  // Take first line only, truncate
  const firstLine = text.split("\n")[0] ?? "";
  return firstLine.length > 120 ? firstLine.slice(0, 120) + "..." : firstLine;
}

/**
 * Wrap email HTML in a full document with CSP to block scripts.
 * Uses <base target="_blank"> so links open in new tabs.
 */
function wrapEmailHtml(html: string): string {
  return `<!DOCTYPE html>
<html>
<head>
  <meta http-equiv="Content-Security-Policy" content="script-src 'none'">
  <base target="_blank">
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      font-size: 14px;
      line-height: 1.5;
      color: #333;
      margin: 0;
      padding: 8px;
      word-break: break-word;
    }
    img { max-width: 100%; height: auto; }
    a { color: #1a73e8; }
    blockquote {
      border-left: 3px solid #ddd;
      margin: 8px 0;
      padding-left: 12px;
      color: #666;
    }
  </style>
</head>
<body>${html}</body>
</html>`;
}

/** Sandboxed iframe that renders email HTML via srcdoc, auto-sized to content. */
const HtmlBody: React.FC<{ html: string }> = ({ html }) => {
  const srcdoc = useMemo(() => wrapEmailHtml(html), [html]);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const resizeToContent = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const doc = iframe.contentDocument;
      if (doc?.body) {
        // Set height to fit content exactly (no internal scrollbar)
        iframe.style.height = doc.body.scrollHeight + "px";
      }
    } catch {
      // Cross-origin fallback — shouldn't happen with allow-same-origin
    }
  }, []);

  const handleLoad = useCallback(() => {
    // Measure once on load
    resizeToContent();
    // Re-measure after a short delay to catch any late-rendering content
    // (e.g. images that haven't loaded yet during initial measurement)
    const timer = setTimeout(resizeToContent, 300);
    return () => clearTimeout(timer);
  }, [resizeToContent]);

  return (
    <iframe
      ref={iframeRef}
      srcDoc={srcdoc}
      className={styles.htmlBodyFrame}
      sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
      title="Email content"
      onLoad={handleLoad}
      referrerPolicy="no-referrer"
    />
  );
};

/** Format file size in human-readable form. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** List of downloadable attachments for a message. */
const AttachmentList: React.FC<{
  attachments: client.EmailThreadAttachment[];
  reportId: string;
  threadUrl: string;
}> = ({ attachments, reportId, threadUrl }) => {
  const [downloading, setDownloading] = useState<string | null>(null);

  const handleDownload = async (att: client.EmailThreadAttachment) => {
    setDownloading(att.attachment_id);
    try {
      const blob = await downloadThreadAttachment(
        reportId,
        threadUrl,
        att.message_id,
        att.attachment_id,
        att.filename
      );
      downloadBlob(att.filename, blob);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className={styles.attachmentList}>
      {attachments.map((att) => (
        <button
          key={att.attachment_id}
          className={styles.attachmentChip}
          onClick={() => handleDownload(att)}
          disabled={downloading === att.attachment_id}
          title={`Download ${att.filename} (${formatFileSize(att.size)})`}
        >
          <i
            className={`pi ${downloading === att.attachment_id ? "pi-spin pi-spinner" : "pi-paperclip"}`}
          />
          <span className={styles.attachmentName}>{att.filename}</span>
          <span className={styles.attachmentSize}>
            {formatFileSize(att.size)}
          </span>
        </button>
      ))}
    </div>
  );
};

/** Count total attachments across all messages in a thread. */
export function getThreadAttachmentCount(
  messages: client.EmailThreadMessage[]
): number {
  let count = 0;
  for (const msg of messages) {
    count += (msg.attachments ?? []).length;
  }
  return count;
}

/** A fully expanded message showing header + body. */
const ExpandedMessage: React.FC<{
  message: client.EmailThreadMessage;
  reportId: string;
  threadUrl: string;
  onCollapse: () => void;
}> = ({ message, reportId, threadUrl, onCollapse }) => {
  const bodyHtml = message.body_html ?? "";
  const bodyText = message.body_text ?? "";
  const hasHtml = bodyHtml.trim().length > 0;
  const hasText = bodyText.trim().length > 0;

  const recipients = [message.to ?? "", message.cc ? `cc: ${message.cc}` : ""]
    .filter(Boolean)
    .join(", ");

  return (
    <div className={styles.messageCard}>
      <div className={styles.messageHeader} onClick={onCollapse}>
        <div className={styles.messageHeaderInfo}>
          <span className={styles.messageSender}>
            {formatSender(message.from ?? "")}
          </span>
          <span className={styles.messageRecipients}>to {recipients}</span>
        </div>
        <span className={styles.messageDate}>
          {formatDate(message.date ?? "")}
        </span>
        <i
          className={`pi pi-chevron-up ${styles.collapseIcon} ${styles.collapseIconExpanded}`}
        />
      </div>
      <div className={styles.messageBody}>
        {hasHtml ? (
          <HtmlBody html={bodyHtml} />
        ) : hasText ? (
          <div className={styles.plainTextBody}>{bodyText}</div>
        ) : (
          <span className={styles.emptyBody}>(No message body)</span>
        )}
        {(message.attachments ?? []).length > 0 && (
          <AttachmentList
            attachments={message.attachments ?? []}
            reportId={reportId}
            threadUrl={threadUrl}
          />
        )}
      </div>
    </div>
  );
};

/** A collapsed message row — just sender, snippet, date. Click to expand. */
const CollapsedMessage: React.FC<{
  message: client.EmailThreadMessage;
  onExpand: () => void;
}> = ({ message, onExpand }) => {
  return (
    <div className={styles.collapsedMessage} onClick={onExpand}>
      <span className={styles.collapsedSender}>
        {formatSender(message.from ?? "")}
      </span>
      <span className={styles.collapsedSnippet}>{getSnippet(message)}</span>
      <span className={styles.collapsedDate}>
        {formatShortDate(message.date ?? "")}
      </span>
    </div>
  );
};

export const EmailThreadModal: React.FC<EmailThreadModalProps> = ({
  visible,
  onHide,
  thread,
  reportId,
}) => {
  // Default state: only the last message is expanded.
  // "all" means the collapsed group was clicked and all messages are visible.
  const [expandedIndices, setExpandedIndices] = useState<Set<number>>(
    new Set()
  );
  const [showAllCollapsed, setShowAllCollapsed] = useState(false);

  // Reset state when thread changes
  useEffect(() => {
    if (thread) {
      setExpandedIndices(new Set([thread.messages.length - 1]));
      setShowAllCollapsed(false);
    }
  }, [thread]);

  if (!thread) return null;

  const { messages } = thread;
  const subject = messages[0]?.subject ?? "Email Thread";

  const toggleExpand = (index: number) => {
    setExpandedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // For threads with >2 messages, show a "N earlier messages" group
  // that can be clicked to reveal the collapsed middle messages.
  const lastIndex = messages.length - 1;
  const middleCount = messages.length - 2; // messages between first and last

  return (
    <Dialog
      header={subject}
      className={styles.dialog}
      visible={visible}
      onHide={onHide}
      modal
      dismissableMask
    >
      <div className={styles.messagesContainer}>
        {messages.map((message, index) => {
          const isExpanded = expandedIndices.has(index);

          // Always show the first and last message.
          // Middle messages are hidden behind a "N earlier messages" group
          // unless showAllCollapsed is true.
          const isFirst = index === 0;
          const isLast = index === lastIndex;
          const isMiddle = !isFirst && !isLast;

          if (isMiddle && !showAllCollapsed && middleCount > 0) {
            // Show the "N earlier messages" indicator only once (at index 1)
            if (index === 1) {
              return (
                <div
                  key="collapsed-group"
                  className={styles.collapsedGroup}
                  onClick={() => setShowAllCollapsed(true)}
                >
                  <i
                    className={`pi pi-chevron-down ${styles.collapsedGroupIcon}`}
                  />
                  {middleCount} earlier{" "}
                  {middleCount === 1 ? "message" : "messages"}
                </div>
              );
            }
            // Hide other middle messages
            return null;
          }

          if (isExpanded) {
            return (
              <ExpandedMessage
                key={index}
                message={message}
                reportId={reportId}
                threadUrl={thread.thread_url}
                onCollapse={() => toggleExpand(index)}
              />
            );
          }

          return (
            <CollapsedMessage
              key={index}
              message={message}
              onExpand={() => toggleExpand(index)}
            />
          );
        })}
      </div>
    </Dialog>
  );
};
